import type { TSetting, TSortAndFilterSettingRole } from '../types'
import { SORT_DIRECTION } from './types'

type Option = { id: string }
type OptionMap<TOption extends Option> = Record<string, TOption>

export const getSortAndFilterSetting = (
    settings: TSetting[],
    group: string,
    role: TSortAndFilterSettingRole,
    scopeId?: string,
    context?: '_main' | '_overview'
): TSetting | undefined =>
    settings.find(
        (setting) =>
            setting.sort_and_filter?.group === group &&
            setting.sort_and_filter.role === role &&
            (setting.sort_and_filter.scope_id ?? '') === (scopeId ?? '') &&
            (setting.sort_and_filter.context ?? '') === (context?.slice(1) ?? '')
    )

export const getOptionsForSetting = <TOption extends Option>(
    options: OptionMap<TOption>,
    setting?: TSetting
): OptionMap<TOption> => {
    if (!setting?.choices.length) return options

    const settingOptions = setting.choices.reduce((acc, choice) => {
        const option = options[choice.choice_key]
        if (option) acc[choice.choice_key] = option
        return acc
    }, {} as OptionMap<TOption>)

    return Object.keys(settingOptions).length ? settingOptions : options
}

export const getSelectedOption = <TOption extends Option>(
    options: OptionMap<TOption>,
    setting: TSetting | undefined,
    fallback: TOption
): TOption => {
    const selectedOption = setting ? options[setting.field_value] : undefined
    if (selectedOption) return selectedOption

    const firstOptionKey = Object.keys(options)[0]
    if (!firstOptionKey) return fallback
    return options[firstOptionKey] ?? fallback
}

const isSortDirection = (value?: string): value is SORT_DIRECTION =>
    value === SORT_DIRECTION.ASC || value === SORT_DIRECTION.DESC

export const getSelectedSortDirection = (setting: TSetting | undefined, fallback: SORT_DIRECTION): SORT_DIRECTION => {
    const fieldValue = setting?.field_value
    if (isSortDirection(fieldValue)) return fieldValue

    const firstDirectionChoice = setting?.choices.find((choice) => isSortDirection(choice.choice_key))?.choice_key
    return isSortDirection(firstDirectionChoice) ? firstDirectionChoice : fallback
}
